/* @ngInject */

export default (
  $rootScope,
  helperService,
  incomeService,
  helperPdfService,
  OwnershipType
) => {
  /**
   * Prepare PAYG income details.
   *
   * @param {Object} contact
   * @param {Object} paygIncome
   * @param {Number} paygCount
   *
   * @returns {Array}
   */
  const preparePaygIncomeDetails = ({ contact, paygIncome, paygCount }) => {
    const content = [];
    if (paygCount) {
      content.push(
        helperPdfService.createSubHeader({
          title: `ANNUAL SALARIED EMPLOYEE INCOME #${paygCount} - ${contact.getName()}`
        })
      );
    }
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Gross salary\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.grossSalary, true) *
                    helperService.getFrequencyValue(paygIncome.payg.grossSalaryFrequency, false)
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Allowance\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.allowance, true) *
                    helperService.getFrequencyValue(paygIncome.payg.allowanceFrequency, false)
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Bonus\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.bonus, true) *
                    helperService.getFrequencyValue(paygIncome.payg.bonusFrequency, false)
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Commission\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.commission, true) *
                    helperService.getFrequencyValue(paygIncome.payg.commissionFrequency, false)
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Overtime essential\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.overtimeEssential, true) *
                    helperService.getFrequencyValue(
                      paygIncome.payg.overtimeEssentialFrequency,
                      false
                    )
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Overtime non essential\n', style: 'label' },
                {
                  text: (
                    helperService.isNumber(paygIncome.payg.overtimeNonEssential, true) *
                    helperService.getFrequencyValue(
                      paygIncome.payg.overtimeNonEssentialFrequency,
                      false
                    )
                  ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare business income details.
   *
   * @param {Object} contact
   * @param {Object} businessIncome
   * @param {Number} businessCount
   * @param {Boolean} showTaxPaid
   *
   * @returns {Array}
   */
  const prepareBusinessIncomeDetails = ({
    contact,
    businessIncome,
    businessCount,
    showTaxPaid
  }) => {
    const content = [];
    if (businessCount) {
      content.push(
        helperPdfService.createSubHeader({
          title: `ANNUAL BUSINESS INCOME #${businessCount} - ${contact.getName()}`
        })
      );
    }
    content.push({
      table: {
        widths: ['48%', '4%', '48%'],
        body: [
          ['', '', ''],
          ['', '', '']
        ]
      },
      layout: 'noBordersNoPadding'
    });
    let bIBodyContent = 0;
    for (let b = 0; b < 2; b += 1) {
      const currentTaxYear = b === 0 ? 'lastTaxYear' : 'previousTaxYear';
      content[content.length - 1].table.body[0][
        bIBodyContent
      ] = helperPdfService.createSubHeader({
        title: `${b === 0 ? 'Last tax year -' : 'Previous tax year -'} ${
          businessIncome?.business[currentTaxYear]?.taxYear || '-'
        }`,
        bodyStyle: ['subSubHeader']
      });
      content[content.length - 1].table.body[1][bIBodyContent] = {
        table: {
          widths: ['50%', '50%'],
          body: [
            [
              {
                text: [
                  { text: 'Gross sales\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.grossSales.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Profit before tax\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.profitBeforeTax.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Depreciation\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.depreciation.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Interest and other addbacks\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.interestAndOtherAddbacks.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Lease rentals\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.leaseRentals.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Amortisation\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.amortisation.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Non recurring expense\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.nonRecurringExpense.formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Loss on sale of fixed asset\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.lossOnSaleOfFixedAsset.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Profit on sale of fixed asset\n', style: 'label' },
                  {
                    text: businessIncome?.business[
                      currentTaxYear
                    ]?.profitOnSaleOfFixedAsset.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: showTaxPaid ? 'Tax paid\n' : '\n', style: 'label' },
                  {
                    text: showTaxPaid
                      ? businessIncome?.business[currentTaxYear]?.taxPaid.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          0
                        )
                      : '',
                    style: 'value'
                  }
                ]
              }
            ]
          ]
        },
        layout: 'noBorders'
      };
      bIBodyContent += 2;
    }
    return content;
  };

    /**
     * Prepare other taxable and non taxable income details.
     *
     * @param {Object} contact
     * @param {Object} otherTaxableOrNonTaxableIncome
     * @param {Number} otherTaxableCount
     * @param {Number} nonTaxableCount
     *
     * @returns {Array}
     */
    const prepareOtherTaxableAndNontaxableIncomeDetails = ({
      contact,
      otherTaxableOrNonTaxableIncome,
      otherTaxableCount,
      nonTaxableCount
    }) => {
      const content = [];
      if (otherTaxableOrNonTaxableIncome.isIncomeTypeOtherTaxable()) {
        content.push(
          helperPdfService.createSubHeader({
            title: `ANNUAL OTHER TAXABLE INCOME #${otherTaxableCount} - ${contact.getName()}`
          })
        );
      }
      if (otherTaxableOrNonTaxableIncome.isIncomeTypeNonTaxable()) {
        content.push(
          helperPdfService.createSubHeader({
            title: `ANNUAL NON TAXABLE INCOME #${nonTaxableCount} - ${contact.getName()}`
          })
        );
      }
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  { text: 'Gross income\n', style: 'label' },
                  {
                    text: (
                      helperService.isNumber(otherTaxableOrNonTaxableIncome.grossValue, true) *
                      helperService.getFrequencyValue(
                        otherTaxableOrNonTaxableIncome.frequency,
                        false
                      )
                    ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Income description\n', style: 'label' },
                  {
                    text: otherTaxableOrNonTaxableIncome.description || '-',
                    style: 'value'
                  }
                ]
              },
              '',
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });
      return content;
    };

  /**
   * Prepare income details per contact.
   *
   * @param {Array} contacts
   * @param {Array} income
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Boolean} showHeader
   *
   * @returns {Array}
   */
  const prepareIncomeDetailsPerContact = ({
    contact,
    income,
    assets,
    homeLoanSecurities,
    showHeader
  }) => {
    const content = [];
    if (contact && income?.length) {
      if (showHeader) {
        content.push(
          helperPdfService.createHeader({
            title: 'INCOME'
          })
        );
      }
      let paygCount = 0;
      let businessCount = 0;
      let otherTaxableCount = 0;
      let nonTaxableCount = 0;
      let existingRental = 0;
      let existingBoarder = 0;
      let newRental = 0;
      let newBoarder = 0;
      const filteredIncome = _.filter(income, (obj) => obj.idContact === contact.id);
      const iLen = filteredIncome.length;
      for (let i = 0; i < iLen; i += 1) {
        const fIncome = filteredIncome[i];
        if (fIncome.isIncomeTypePayg()) {
          paygCount += 1;
          content.push(preparePaygIncomeDetails({ contact, paygIncome: fIncome, paygCount }));
        }

        if (fIncome.isIncomeTypeBusiness()) {
          businessCount += 1;
          content.push(
            prepareBusinessIncomeDetails({
              contact,
              businessIncome: fIncome,
              businessCount,
              showTaxPaid: true
            })
          );
        }

        if (fIncome.isIncomeTypeOtherTaxable() || fIncome.isIncomeTypeNonTaxable()) {
          if (fIncome.isIncomeTypeOtherTaxable()) {
            otherTaxableCount += 1;
            content.push(
              prepareOtherTaxableAndNontaxableIncomeDetails({
                contact,
                otherTaxableOrNonTaxableIncome: fIncome,
                otherTaxableCount
              })
            );
          }
          if (fIncome.isIncomeTypeNonTaxable()) {
            nonTaxableCount += 1;
            content.push(
              prepareOtherTaxableAndNontaxableIncomeDetails({
                contact,
                otherTaxableOrNonTaxableIncome: fIncome,
                nonTaxableCount
              })
            );
          }
        }
      }

      const investmentAssets = _.filter(
        assets,
        (obj) =>
          helperService.isAssetTypeInvestment(obj.assetType) &&
          helperService.isNumber(obj.monthlyRentalIncome, true) > 0 &&
          helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            obj.ownership,
            contact.id
          ) > 0
      );

      for (let r = 0; r < investmentAssets.length; r += 1) {
        const investment = investmentAssets[r];
        existingRental += 1;
        content.push(
          helperPdfService.createSubHeader({
            title: `${
              helperService.getOrganizationCountry().cca2 === 'NZ' ? 'WEEKLY' : 'ANNUAL'
            } EXISTING RENTAL INCOME #${existingRental} - ${contact.getName()}`
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                {
                  text: [
                    { text: 'Rental income\n', style: 'label' },
                    {
                      text: (
                        (helperService.isNumber(investment.monthlyRentalIncome, true) *
                          (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                            investment.ownership,
                            contact.id
                          ) /
                            100) *
                          12) /
                        (helperService.getOrganizationCountry().cca2 === 'NZ' ? 52 : 1)
                      ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Asset\n', style: 'label' },
                    {
                      text: investment.getName() || '-',
                      style: 'value'
                    }
                  ],
                  colSpan: 2
                },
                '',
                {
                  text: [
                    { text: 'Ownership\n', style: 'label' },
                    {
                      text: `${helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                        investment.ownership,
                        contact.id
                      )}%`,
                      style: 'value'
                    }
                  ]
                }
              ]
            ]
          },
          layout: 'noBorders'
        });
      }

      if (helperService.getOrganizationCountry().cca2 === 'NZ') {
        const ownerOccupiedAssets = _.filter(
          assets,
          (obj) =>
            helperService.isAssetTypeOwnerOccupied(obj.assetType) &&
            helperService.isNumber(obj.monthlyRentalIncome, true) > 0 &&
            helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
              obj.ownership,
              contact.id
            ) > 0
        );

        for (let b = 0; b < ownerOccupiedAssets.length; b += 1) {
          const ownerOccupied = ownerOccupiedAssets[b];
          existingBoarder += 1;
          content.push(
            helperPdfService.createSubHeader({
              title: `WEEKLY EXISTING BOARDER INCOME #${existingBoarder} - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Boarder income\n', style: 'label' },
                      {
                        text: (
                          (helperService.isNumber(ownerOccupied.monthlyRentalIncome, true) *
                            (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                              ownerOccupied.ownership,
                              contact.id
                            ) /
                              100) *
                            12) /
                          52
                        ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Asset\n', style: 'label' },
                      {
                        text: ownerOccupied.getName() || '-',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Ownership\n', style: 'label' },
                      {
                        text: `${helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                          ownerOccupied.ownership,
                          contact.id
                        )}%`,
                        style: 'value'
                      }
                    ]
                  }
                ]
              ]
            },
            layout: 'noBorders'
          });
        }
      }

      const investmentHomeLoanSecurities = _.filter(
        homeLoanSecurities,
        (obj) =>
          obj.isNew &&
          obj.ownershipType?.name === OwnershipType.investment.name &&
          helperService.isNumber(obj.monthlyRentalIncome, true) > 0 &&
          helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            obj.ownership,
            contact.id
          ) > 0
      );

      for (let r = 0; r < investmentHomeLoanSecurities.length; r += 1) {
        const investment = investmentHomeLoanSecurities[r];
        newRental += 1;
        content.push(
          helperPdfService.createSubHeader({
            title: `${
              helperService.getOrganizationCountry().cca2 === 'NZ' ? 'WEEKLY' : 'ANNUAL'
            } NEW RENTAL INCOME #${newRental} - ${contact.getName()}`
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                {
                  text: [
                    { text: 'Rental income\n', style: 'label' },
                    {
                      text: (
                        (helperService.isNumber(investment.monthlyRentalIncome, true) *
                          (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                            investment.ownership,
                            contact.id
                          ) /
                            100) *
                          12) /
                        (helperService.getOrganizationCountry().cca2 === 'NZ' ? 52 : 1)
                      ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Asset\n', style: 'label' },
                    {
                      text: investment.getName() || '-',
                      style: 'value'
                    }
                  ],
                  colSpan: 2
                },
                '',
                {
                  text: [
                    { text: 'Ownership\n', style: 'label' },
                    {
                      text: `${helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                        investment.ownership,
                        contact.id
                      )}%`,
                      style: 'value'
                    }
                  ]
                }
              ]
            ]
          },
          layout: 'noBorders'
        });
      }

      if (helperService.getOrganizationCountry().cca2 === 'NZ') {
        const ownerOccupiedHomeLoanSecurities = _.filter(
          homeLoanSecurities,
          (obj) =>
            obj.isNew &&
            obj.ownershipType?.name === OwnershipType.owner_occupied.name &&
            helperService.isNumber(obj.monthlyRentalIncome, true) > 0 &&
            helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
              obj.ownership,
              contact.id
            ) > 0
        );

        for (let b = 0; b < ownerOccupiedHomeLoanSecurities.length; b += 1) {
          const ownerOccupied = ownerOccupiedHomeLoanSecurities[b];
          newBoarder += 1;
          content.push(
            helperPdfService.createSubHeader({
              title: `WEEKLY NEW BOARDER INCOME #${newBoarder} - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Boarder income\n', style: 'label' },
                      {
                        text: (
                          (helperService.isNumber(ownerOccupied.monthlyRentalIncome, true) *
                            (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                              ownerOccupied.ownership,
                              contact.id
                            ) /
                              100) *
                            12) /
                          52
                        ).formatWithCurrency($rootScope.CurrentCurrency(), 0),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Asset\n', style: 'label' },
                      {
                        text: ownerOccupied.getName() || '-',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Ownership\n', style: 'label' },
                      {
                        text: `${helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
                          ownerOccupied.ownership,
                          contact.id
                        )}%`,
                        style: 'value'
                      }
                    ]
                  }
                ]
              ]
            },
            layout: 'noBorders'
          });
        }
      }
    }
    return content;
  };

  /**
   * Prepare income details.
   *
   * @param {Object} contact
   * @param {Array} income
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const prepareTotalIncomeDetailsPerContact = ({
    contact,
    income,
    assets,
    homeLoanSecurities,
    workflow
  }) => {
    const availableIncomeTypes = incomeService.getAvailableIncomeTypes();
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `ANNUAL INCOME SUMMARY - ${contact.getName()}`
      })
    );
    if (!contact.isCompany) {
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  { text: 'Total salaried employee income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[0].id,
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total other taxable income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[2].id,
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total non taxable income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[3].id,
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total rental income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: 'rental',
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              }
            ]
          ]
        },
        layout: 'noBorders'
      });

      if (
        helperService.getOrganizationCountry().cca2 === 'NZ' ||
        contact.isCurrentEmployerSelfEmployed()
      ) {
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [['', '', '', '']]
          },
          layout: 'noBorders'
        });
        if (helperService.getOrganizationCountry().cca2 === 'NZ') {
          content[content.length - 1].table.body[0][0] = {
            text: [
              { text: 'Total boarder income\n', style: 'label' },
              {
                text: incomeService
                  .getTotalIncomeByContactByIncomeType({
                    income,
                    incomeType: 'boarder',
                    businessYear: null,
                    isBusinessDepreciation: null,
                    idContact: contact.id,
                    assets,
                    homeLoanSecurities,
                    workflowType: workflow.type,
                    isDealOwnerBroker: true,
                    haveLendersExisting: true,
                    isUsingForFinanceCalculations: false
                  })
                  .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                style: 'value'
              }
            ]
          };
        }

        if (contact.isCurrentEmployerSelfEmployed()) {
          content[content.length - 1].table.body[0][
            helperService.getOrganizationCountry().cca2 === 'NZ' ? 1 : 0
          ] = {
            text: [
              {
                text: !workflow.isAssetFinance()
                  ? 'Total business income\n'
                  : 'Total business income (last year)\n',
                style: 'label'
              },
              {
                text: incomeService
                  .getTotalIncomeByContactByIncomeType({
                    income,
                    incomeType: availableIncomeTypes[1].id,
                    businessYear: workflow.isAssetFinance() ? 'lastTaxYear' : null,
                    isBusinessDepreciation: false,
                    idContact: contact.id,
                    assets,
                    homeLoanSecurities,
                    workflowType: workflow.type,
                    isDealOwnerBroker: true,
                    haveLendersExisting: true,
                    isUsingForFinanceCalculations: false
                  })
                  .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                style: 'value'
              }
            ]
          };

          content[content.length - 1].table.body[0][
            helperService.getOrganizationCountry().cca2 === 'NZ' ? 2 : 1
          ] = {
            text: [
              {
                text: !workflow.isAssetFinance()
                  ? 'Total depreciation income\n'
                  : 'Total business income (previous year)\n',
                style: 'label'
              },
              {
                text: incomeService
                  .getTotalIncomeByContactByIncomeType({
                    income,
                    incomeType: availableIncomeTypes[1].id,
                    businessYear: workflow.isAssetFinance() ? 'previousTaxYear' : null,
                    isBusinessDepreciation: !workflow.isAssetFinance(),
                    idContact: contact.id,
                    assets,
                    homeLoanSecurities,
                    workflowType: workflow.type,
                    isDealOwnerBroker: true,
                    haveLendersExisting: true,
                    isUsingForFinanceCalculations: false
                  })
                  .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                style: 'value'
              }
            ]
          };
          if (helperService.getOrganizationCountry().cca2 !== 'NZ') {
            content[content.length - 1].table.body[0][3] = '';
          } else {
            content[content.length - 1].table.body[0][2] = '';
            content[content.length - 1].table.body[0][3] = '';
          }
        }
      }
    } else {
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  {
                    text: !workflow.isAssetFinance()
                      ? 'Total business income\n'
                      : 'Total business income (last year)\n',
                    style: 'label'
                  },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[1].id,
                        businessYear: workflow.isAssetFinance() ? 'lastTaxYear' : null,
                        isBusinessDepreciation: false,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  {
                    text: !workflow.isAssetFinance()
                      ? 'Total depreciation income\n'
                      : 'Total business income (previous year)\n',
                    style: 'label'
                  },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[1].id,
                        businessYear: workflow.isAssetFinance() ? 'previousTaxYear' : null,
                        isBusinessDepreciation: !workflow.isAssetFinance(),
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total other taxable income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[2].id,
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total non taxable income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: availableIncomeTypes[3].id,
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Total rental income\n', style: 'label' },
                  {
                    text: incomeService
                      .getTotalIncomeByContactByIncomeType({
                        income,
                        incomeType: 'rental',
                        businessYear: null,
                        isBusinessDepreciation: null,
                        idContact: contact.id,
                        assets,
                        homeLoanSecurities,
                        workflowType: workflow.type,
                        isDealOwnerBroker: true,
                        haveLendersExisting: true,
                        isUsingForFinanceCalculations: false
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              '',
              '',
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });
    }
    return content;
  };
  /**
   * Prepare income details.
   *
   * @param {Array} contacts
   * @param {Array} income
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const prepareIncomeDetails = ({ contacts, income, assets, homeLoanSecurities, workflow }) => {
    const content = [];
    if (contacts?.length && income?.length) {
      content.push({
        text: '',
        margin: [0, 20, 0, 0]
      });
      content.push(
        helperPdfService.createHeader({
          title: 'INCOME',
          pageBreak: ''
        })
      );
      const len = contacts.length;
      for (let c = 0; c < len; c += 1) {
        const contact = contacts[c];
        content.push(
          prepareIncomeDetailsPerContact({
            contact,
            income,
            assets,
            homeLoanSecurities
          })
        );
      }
      const cLen = contacts.length;
      for (let j = 0; j < cLen; j += 1) {
        const contact = contacts[j];
        content.push(
          prepareTotalIncomeDetailsPerContact({
            contact,
            income,
            assets,
            homeLoanSecurities,
            workflow
          })
        );
      }
    }
    return content;
  };

  return {
    prepareIncomeDetails,
    prepareIncomeDetailsPerContact,
    prepareTotalIncomeDetailsPerContact
  };
};
